/**
 * BaseBundleSelector
 * Shared base class for bundle builders (page and modal).
 * Handles cart interactions, quantity calculations, state tracking, and UI filtering primitives.
 */
class BaseBundleSelector {
  constructor(options = {}) {
    this.options = options;
    this.selectedProducts = new Map();
    this.variantSelections = new Map();
    this.currentVariantId = null;
    this.maxItems = 0;
    this.maxPremiumItems = 0;
    this.currentVariantData = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.bindProductCardEvents();
    this.bindSearch();
    this.bindTagFilters();
    this.updatePreview();
  }

  bindTagFilters() {
    const checkboxes = document.querySelectorAll('.bundle-filter-item input[type="checkbox"]');
    if (!checkboxes.length) return;

    checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        this.applyTagFilters();
      });
    });
  }

  applyTagFilters() {
    const activeTags = Array.from(document.querySelectorAll('.bundle-filter-item input[type="checkbox"]:checked')).map(
      (cb) => cb.dataset.productTag.toLowerCase()
    );

    const productCards = document.querySelectorAll('#productGrid .product-candy-card');
    const noResultsMessage = document.getElementById('noResultsMessage');

    let visibleCount = 0;

    productCards.forEach((card) => {
      const cardTags = (card.dataset.productTags || '').toLowerCase();
      const matches = activeTags.every((tag) => cardTags.includes(tag));

      if (activeTags.length === 0 || matches) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    if (noResultsMessage) {
      noResultsMessage.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  bindSearch() {
    const searchInputs = document.querySelectorAll('#productSearchInput, .bundle-search-input');
    if (!searchInputs.length) return;

    searchInputs.forEach((searchInput) => {
      searchInput.addEventListener('input', (e) => {
        const query = e.target.value.toLowerCase().trim();
        this.filterProducts(query);
      });
    });
  }

  matchesCard(card, query, activeTags) {
    const title = (card.dataset.productTitle || '').toLowerCase();
    const cardTags = (card.dataset.productTags || '').toLowerCase();

    const matchesSearch = query === '' || title.includes(query);
    const matchesTags = activeTags.length === 0 || activeTags.every((tag) => cardTags.includes(tag));

    return matchesSearch && matchesTags;
  }

  filterProducts(query = '') {
    const productCards = document.querySelectorAll('#productGrid .product-candy-card');
    const noResultsMessage = document.getElementById('noResultsMessage');

    const activeTags = Array.from(document.querySelectorAll('.bundle-filter-item input[type="checkbox"]:checked')).map(
      (cb) => cb.dataset.productTag.toLowerCase()
    );

    let visibleCount = 0;

    productCards.forEach((card) => {
      if (this.matchesCard(card, query, activeTags)) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    if (noResultsMessage) {
      noResultsMessage.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  bindEvents() {
    const atcBtn = document.getElementById('addBundleToCart');
    if (atcBtn) {
      atcBtn.addEventListener('click', () => {
        this.addBundleToCart();
      });
    }
  }

  selectVariant(variantBtn) {
    const variantId = variantBtn.dataset.variantId;

    this.currentVariantId = variantId;
    this.maxItems = parseInt(variantBtn.dataset.totalCandy || variantBtn.dataset.ownTotalCandy || '0', 10);
    this.maxPremiumItems = parseInt(variantBtn.dataset.premiumCandy || variantBtn.dataset.ownPremiumCandy || '0', 10);
    const titleEl = variantBtn.dataset.variantTitle;

    this.currentVariantData = {
      id: variantId,
      title: titleEl || 'Selected Bundle',
      price: variantBtn.dataset.variantPrice,
      totalCandy: this.maxItems,
      premiumCandy: this.maxPremiumItems,
    };

    this.loadVariantSelections();
    this.updateVariantInfo();
    this.updatePreview();
    this.updateAddToCartButton();
  }

  updateVariantInfo() {
    if (this.currentVariantData) {
      const totalcountElm = document.querySelectorAll('.total-count');
      if (totalcountElm) {
        totalcountElm.forEach((el) => {
          el.textContent = this.maxItems;
        });
      }
      const premiumcountElm = document.querySelectorAll('.premium-count');
      if (premiumcountElm) {
        premiumcountElm.forEach((el) => {
          el.textContent = this.maxPremiumItems;
        });
      }
    }
  }

  bindProductCardEvents() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-action]');
      if (!button) return;

      const card = button.closest('.product-candy-card');
      if (!card) return;

      const action = button.dataset.action;
      const productId = card.dataset.productId;

      this.handleProductAction(productId, action, card);
    });
  }

  getProductDataFromCard(card) {
    return {
      id: String(card.dataset.productId),
      title: card.dataset.productTitle,
      price: card.dataset.productPrice,
      image: card.dataset.productImg || card.dataset.productImage,
      variant_id: card.dataset.productVariant,
      collection: card.dataset.collection || card.dataset.productCollection,
    };
  }

  handleProductAction(productId, action, card) {
    if (!this.currentVariantId) {
      alert('Please select a bundle size first.');
      return;
    }

    productId = String(productId);

    switch (action) {
      case 'add': {
        const product = this.getProductDataFromCard(card);
        this.addProduct(product);
        break;
      }
      case 'increase':
        this.increaseQuantity(productId);
        break;
      case 'decrease':
        this.decreaseQuantity(productId);
        break;
    }
  }

  getProductCard(productId) {
    return document.querySelector(`.product-grid [data-product-id="${productId}"]`);
  }

  canAddProduct(product) {
    if (!this.currentVariantId) return false;

    const currentTotal = this.getCurrentTotalCount();
    if (currentTotal >= this.maxItems) {
      this.showLimitMessage();
      return false;
    }

    if (product.collection === 'premium') {
      const currentPremium = this.getCurrentPremiumCount();
      if (currentPremium >= this.maxPremiumItems) {
        this.showLimitMessage(product);
        return false;
      }
    }

    return true;
  }

  canIncreaseQuantity(productId) {
    productId = String(productId);
    const product = this.selectedProducts.get(productId);
    if (!product) return false;

    const currentTotal = this.getCurrentTotalCount();
    if (currentTotal >= this.maxItems) {
      this.showLimitMessage();
      return false;
    }

    if (product.collection === 'premium') {
      const currentPremium = this.getCurrentPremiumCount();
      if (currentPremium >= this.maxPremiumItems) {
        this.showLimitMessage(product);
        return false;
      }
    }

    return true;
  }

  getCurrentTotalCount() {
    let total = 0;
    this.selectedProducts.forEach((product) => {
      total += product.quantity;
    });
    return total;
  }

  getCurrentPremiumCount() {
    let count = 0;
    this.selectedProducts.forEach((product) => {
      if (product.collection === 'premium') {
        count += product.quantity;
      }
    });
    return count;
  }

  getPremiumCount() {
    return this.getCurrentPremiumCount();
  }

  showLimitMessage(product = null) {
    if (product && product.collection === 'premium') {
      alert(`You can only select up to ${this.maxPremiumItems} premium items for this bundle.`);
    } else {
      alert(`You can only select up to ${this.maxItems} items for this bundle.`);
    }
  }

  addProduct(product) {
    if (!this.canAddProduct(product)) return;

    const id = String(product.id);

    if (this.selectedProducts.has(id)) {
      const existing = this.selectedProducts.get(id);
      existing.quantity++;
      this.selectedProducts.set(id, existing);
    } else {
      this.selectedProducts.set(id, {
        ...product,
        quantity: 1,
      });
    }

    this.saveVariantSelections();
    this.updatePreview();
    this.updateAddToCartButton();

    const card = this.getProductCard(id);
    if (card) this.updateProductCardUI(card, id);
    this.updateProgressUI();
  }

  increaseQuantity(productId) {
    productId = String(productId);
    const product = this.selectedProducts.get(productId);
    if (!product) return;

    if (product.collection === 'premium') {
      const currentPremium = this.getPremiumCount();
      if (currentPremium >= this.maxPremiumItems) {
        alert(`You can only add ${this.maxPremiumItems} premium candies to this mix.`);
        return;
      }
    }

    product.quantity++;
    this.selectedProducts.set(productId, product);

    this.saveVariantSelections();
    this.updatePreview();
    this.updateAddToCartButton();

    const card = document.querySelector(`[data-product-id="${productId}"]`);
    if (card) this.updateProductCardUI(card, productId);
    this.updateProgressUI();
  }

  decreaseQuantity(productId) {
    productId = String(productId);
    const product = this.selectedProducts.get(productId);
    if (!product) return;

    if (product.quantity > 1) {
      product.quantity--;
      this.selectedProducts.set(productId, product);
    } else {
      this.selectedProducts.delete(productId);
    }

    this.saveVariantSelections();
    this.updatePreview();
    this.updateAddToCartButton();

    const card = this.getProductCard(productId);
    if (card) this.updateProductCardUI(card, productId);
    this.updateProgressUI();
  }

  updateProductCardUI(card, productId) {
    productId = String(productId);
    const product = this.selectedProducts.get(productId);
    const quantity = product ? product.quantity : 0;
    const isSelected = this.selectedProducts.has(productId);

    const actionsDiv = card.querySelector('.product-card-actions');
    if (!actionsDiv) return;

    if (isSelected && quantity > 0) {
      card.classList.add('filled');
      actionsDiv.innerHTML = `
        <div class="quantity-controls">
          <button class="qty-btn minus" data-action="decrease">-</button>
          <span class="quantity">${quantity}</span>
          <button class="qty-btn plus" data-action="increase">+</button>
        </div>
      `;
    } else {
      card.classList.remove('filled');
      actionsDiv.innerHTML = `
        <button class="add-btn" data-action="add">+ Add</button>
      `;
    }
  }

  renderPreviewItem(product) {
    return `
      <div class="preview-item-inner">
        <div class="preview-img-title">
          <div class="preview-item-bg">
            <img src="${product.image}" alt="${product.title}" style="width: 100%; height: 100%; object-fit: cover;">
          </div>
          <h5 class="preview-title">${product.title}</h5>
        </div>
        <div class="preview-item-controls">
          <span class="preview-item-count">1</span>
          <button class="remove-btn" data-product-id="${product.id}">×</button>
        </div>
      </div>
    `;
  }

  updatePreview() {
    const preview = document.getElementById('selectedPreview');
    if (!preview) return;

    preview.innerHTML = '';

    const totalSlots = this.maxItems || 0;
    let filledCount = 0;

    this.selectedProducts.forEach((product) => {
      for (let i = 0; i < product.quantity && filledCount < totalSlots; i++) {
        const item = document.createElement('div');
        item.className = 'preview-item filled';
        item.innerHTML = this.renderPreviewItem(product);

        item.querySelector('.remove-btn').addEventListener('click', (e) => {
          e.stopPropagation();
          this.decreaseQuantity(product.id);
        });

        preview.appendChild(item);
        filledCount++;
      }
    });

    for (let i = filledCount; i < totalSlots; i++) {
      const emptySlot = document.createElement('div');
      emptySlot.className = 'preview-item empty';
      emptySlot.innerHTML = '<span class="empty-icon">+</span>';
      preview.appendChild(emptySlot);
    }

    if (typeof window.initCustomHorizontalScroll === 'function') {
      window.initCustomHorizontalScroll('.selected-preview-list');
    }
    this.updateSelectedCount(filledCount);
  }

  updateSelectedCount(count) {
    document.querySelectorAll('.selected-count').forEach((el) => {
      el.textContent = count;
    });
  }

  removeProduct(productId) {
    productId = String(productId);
    this.selectedProducts.delete(productId);

    this.saveVariantSelections();
    this.updatePreview();
    this.updateAddToCartButton();

    const card = this.getProductCard(productId);
    if (card) this.updateProductCardUI(card, productId);

    this.updateProgressUI();
  }

  saveVariantSelections() {
    if (this.currentVariantId) {
      const selections = Array.from(this.selectedProducts.entries());
      this.variantSelections.set(this.currentVariantId, selections);
    }
  }

  updateAllProductCardsUI() {
    const allCards = document.querySelectorAll('.product-candy-card');
    allCards.forEach((card) => {
      const productId = card.dataset.productId;
      this.updateProductCardUI(card, productId);
    });
  }

  loadVariantSelections() {
    this.selectedProducts.clear();
    if (this.currentVariantId && this.variantSelections.has(this.currentVariantId)) {
      const selections = this.variantSelections.get(this.currentVariantId);
      this.selectedProducts = new Map(selections);
    }
    this.updateAllProductCardsUI();
    this.updateProgressUI();
  }

  updateAddToCartButton() {
    const addToCartBtn = document.getElementById('addBundleToCart');
    if (!addToCartBtn) return;

    const currentTotal = this.getCurrentTotalCount();
    const isValid = currentTotal === this.maxItems && this.currentVariantId;

    addToCartBtn.disabled = !isValid;
    addToCartBtn.classList.toggle('disabled', !isValid);
  }

  updateProgressUI() {
    const progressBar = document.querySelector('.bundle-progress-bar');
    if (!progressBar || !this.maxItems) return;

    const selectedCount = this.getCurrentTotalCount();
    const percentage = Math.min((selectedCount / this.maxItems) * 100, 100);
    progressBar.style.width = percentage + '%';
  }

  addToCart() {
    if (this.selectedProducts.size === 0 || !this.currentVariantId) return;

    const cartData = {
      variantId: this.currentVariantId,
      selectedProducts: Array.from(this.selectedProducts.values()),
    };

    alert(`Bundle added to cart! ${this.selectedProducts.size} items selected for ${this.currentVariantData.title}.`);
  }

  async addBundleToCart() {
    if (!this.currentVariantId) {
      alert('Please select a variant first.');
      return;
    }

    // Build line item properties from selected products
    const properties = {};
    let index = 1;
    this.selectedProducts.forEach((product) => {
      properties[`Candy ${index}`] = `${product.title} (x${product.quantity})`;
      index++;
    });

    // Build cart payload
    const data = {
      id: this.currentVariantId,
      quantity: 1,
      properties: properties,
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      // Optionally refresh cart UI or open drawer
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.refresh();
        drawer.hideLoader();
        drawer.open();
      } else {
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('Error adding bundle:', error);
      alert('Something went wrong adding your mix. Please try again.');
    }
  }
}

window.BaseBundleSelector = BaseBundleSelector;
